import React from 'react';
import { Chip, Box, SxProps, Theme, Button } from '@mui/material';
import { Palette, PaletteColor, useTheme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import { getOutlinedHoverBg, getBackgroundColor, getContentColor } from '/packages/shared-components/utils/color';
import { commonTokens } from '/packages/shared-components/assets/themes/tokens';

type variant = 'solid' | 'subtle' | 'outlined';

export interface StatusChipProps {
  label: React.ReactNode;
  color?: 'primary' | 'success' | 'error' | 'info' | 'warning' | 'none' | 'indigo' | 'gray';
  size?: 'medium' | 'small' | undefined;
  variant?: variant;

  icon?: React.ReactNode;
  onDelete?: (event: any) => void;
  sx?: SxProps<Theme>;
}

export const removeUnderscores = (label: string) => label?.split('_').join(' ') || '';
export const getDisplayValue = (label: React.ReactNode) => {
  if (typeof label === 'string') {
    const str = removeUnderscores(label);
    return str;
  }
  return label;
  // return str.replace(/([A-Z])/g, ' $1').trim();
};

type colorPropType = keyof PaletteColor;

const getBgColor = (color: PaletteColor, variant: variant, colorProp: colorPropType) => {
  if (variant === 'subtle') return getBackgroundColor(color);
  if (variant === 'outlined') return 'transparent';
  return color[colorProp];
};

const getTextColor = (color: PaletteColor, variant: variant, colorProp: colorPropType): string => {
  if (variant === 'subtle') return getContentColor(color);
  if (variant === 'solid') return color.contrastText;
  return color[colorProp];
};

// TODO: improve to a hook
export const getChipStyles = (color: StatusChipProps['color'], variant: variant, palette: Palette) => {
  switch (color) {
    case 'success':
      return {
        background: getBgColor(palette.success, variant, 'main'),
        color: getTextColor(palette.success, variant, 'main'),
        borderColor: variant === 'outlined' ? palette.success.main : undefined,
      };
    case 'info':
      return {
        background: getBgColor(palette.info, variant, 'main'),
        color: getTextColor(palette.info, variant, 'main'),
        borderColor: variant === 'outlined' ? palette.info.main : undefined,
      };
    case 'error':
      return {
        background: getBgColor(palette.error, variant, 'main'),
        color: getTextColor(palette.error, variant, 'main'),
        borderColor: variant === 'outlined' ? palette.error.main : undefined,
      };
    case 'primary':
      return {
        background: getBgColor(palette.primary, variant, 'main'),
        color: getTextColor(palette.primary, variant, 'main'),
        borderColor: variant === 'outlined' ? palette.primary.main : undefined,
      };
    case 'warning':
      return {
        background: getBgColor(palette.warning, variant, 'main'),
        color: getTextColor(palette.warning, variant, 'main'),
        borderColor: variant === 'outlined' ? palette.warning.main : undefined,
      };
    case 'indigo':
      return {
        background: getBgColor(palette.indigo, variant, 'main'),
        color: getTextColor(palette.indigo, variant, 'main'),
        borderColor: variant === 'outlined' ? palette.indigo.main : undefined,
      };
    case 'gray':
      return {
        background: {
          solid: 'rgba(81, 89, 108, 1)',
          subtle: palette.mode === 'dark' ? commonTokens.componentDark.primary.background : commonTokens.gray.gray05,
          outlined: palette.mode === 'dark' ? 'transparent' : 'white',
        }[variant],
        color: {
          solid: 'white',
          subtle:
            palette.mode === 'dark'
              ? commonTokens.componentDark.primary.content
              : commonTokens.component.primary.content,
          outlined: palette.mode === 'dark' ? 'rgba(247, 247, 249, 0.87)' : 'rgba(81, 89, 108, 1)',
        }[variant],
        borderColor: variant === 'outlined' ? 'rgba(81, 89, 108, 1)' : undefined,
      };
    default:
      return { background: getOutlinedHoverBg(palette.primary), color: palette.text.primary };
  }
};

/**
 * Figma Component: Chips
 * @returns {JSX.Element}
 */
const StatusChip = ({ label, color = 'none', size, variant = 'solid', sx, icon, onDelete }: StatusChipProps) => {
  const theme = useTheme();
  const chipVariant = variant !== 'outlined' ? 'filled' : 'outlined';
  const baseStyles = getChipStyles(color, variant, theme.palette);

  return (
    <Chip
      label={getDisplayValue(label)}
      size={size}
      variant={chipVariant}
      onDelete={onDelete}
      sx={[
        baseStyles,
        { lineHeight: 1 },
        ...(typeof label === 'string' ? [{ '> span': { px: 1.5 } }] : []),
        ...(icon ? [{ '> .MuiChip-icon': { mr: '5px', ml: '-6px' } }] : []),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      icon={
        icon ? (
          <Box order={2} sx={{ color: `${baseStyles.color} !important` }}>
            {icon}
          </Box>
        ) : undefined
      }
      data-cy="status-chip"
    />
  );
};

interface ButtonChipProps {
  label: string;
  variant?: variant;
  color?: StatusChipProps['color'];
  size?: StatusChipProps['size'];
  onClick?: () => Promise<void> | void;
  sx?: SxProps;
}

export const ButtonChip = ({ label, color = 'none', variant = 'solid', size, onClick, sx }: ButtonChipProps) => {
  const theme = useTheme();
  const handleOnClick = () => {
    if (onClick) onClick();
  };

  const baseStyles = getChipStyles(color, variant, theme.palette);
  const lineHeight = size === 'small' ? '20px' : undefined;

  if (!onClick) {
    return <StatusChip label={label} size={size} color={color} sx={sx} variant={variant} />;
  }

  return (
    <Button
      sx={[
        baseStyles,
        { borderRadius: '100px', lineHeight, py: '2px', letterSpacing: 'normal' },
        { '.MuiButton-startIcon': { marginRight: 0 } },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      size={size}
      onClick={handleOnClick}
      startIcon={<DownloadIcon />}
    >
      <Box px={0.75}>{label}</Box>
    </Button>
  );
};

// TODO: create a general chip with the colors and then use it for StatusChip

export default StatusChip;
